<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { writable } from 'svelte/store'
    import type { Writable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import type { DateTime } from 'luxon'

    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import { lg as currentLg, translatedSupportedLanguages } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as ugroups } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import GenericListLegend from 'hydria/components/GenericListLegend.svelte'
    import Modal from 'hydria/components/Modal.svelte'

    const singular = 'block'
    const plural = 'blocks'
    const baseUrl = '/blocks'
    const jsonBaseUrl = `/json/cms/1/${plural}`

    const pageTitle = tgettext('Blocs de texte')
    const limit = 0

    interface IRevision {
        id: string
        lg: string
        active: boolean
        ctime: DateTime
        author_id: string
        title: string
    }
    interface IRevisionGroup {
        nb_revs: number
        latest: IRevision
        online: IRevision

        mainTag?: 'online' | 'latest' | 'missing'
        main?: IRevision
    }
    interface IDocument {
        id: string
        ctime: DateTime
        description: string
        online_revisions: {
            id: string
            lg: string
        }[]
        lgs: {
            en: IRevisionGroup
            fr: IRevisionGroup
        }
    }

    // Search result from the server
    interface ISearchResult {
        items: IDocument[]
        total_items: number
    }

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    onMount(() => {
        doSearch()
    })

    let results: Writable<IDocument[]> = writable([])

    let updating = $state(false)
    let loading = $state(true)
    let searchId = 0

    function doSearch() {
        updating = true

        let sdata: { [key: string]: any } = {}
        // if (sOptions.q != '') sdata.text = sOptions.q
        sdata.limit = limit
        // if (sOptions.s_by != '') sdata.sort_by = sOptions.s_by
        // sdata.sort_asc = !sOptions.s_desc

        searchId++
        ;((localSearchId) => {
            // simpleFetch('GET', jsonBaseUrl, { data: JSON.stringify(sdata) }).then((r) => {
            simpleFetch('GET', jsonBaseUrl, sdata)
                .then((r) => {
                    if (localSearchId != searchId) {
                        // This result does not match the current search anymore
                        // and there should be another search in progress
                        console.warn('Received search result for an older search, ignoring')
                        return
                    }
                    loadSearchResult(r.data as ISearchResult)
                    updating = false
                })
                .catch((e) => {
                    console.error('Error loading search results:', e)
                    notify({ title: $_('Search'), text: $_('Failed to load search results'), type: 'danger' })
                    updating = false
                })
        })(searchId)
    }

    let nItems = 0

    function loadSearchResult(data: ISearchResult) {
        let litems: any[] = []

        nItems = data.total_items

        for (const item of data.items) {
            if (item.ctime) item.ctime = toLuxon(item.ctime)

            for (const lg of ['en', 'fr']) {
                if (!item.lgs[lg]) {
                    item.lgs[lg] = {
                        mainTag: 'missing',
                        main: null,
                    }
                } else if (item.lgs[lg].online != null) {
                    item.lgs[lg].mainTag = 'online'
                    item.lgs[lg].main = item.lgs[lg].online
                } else if (item.lgs[lg].latest != null) {
                    item.lgs[lg].mainTag = 'latest'
                    item.lgs[lg].main = item.lgs[lg].latest
                } else {
                    item.lgs[lg].mainTag = 'missing'
                    item.lgs[lg].main = null
                }
            }

            litems.push(item)
        }

        results.set(litems)
        if (loading) loading = false
    }

    const crumbs = [{ txt: pageTitle, url: `#${baseUrl}` }]

    let showCreate = $state(false)
    let creating = $state(false)

    interface CreateData {
        mode: 'html' | 'markdown' | 'text'
        name: string
        desc: string
    }
    let createData: CreateData | undefined = $state(undefined)

    function openCreateModal() {
        createData = {
            mode: 'html',
            name: '',
            desc: '',
        }
        showCreate = true
    }
    function createIsValid(createData: CreateData) {
        return createData.name && createData.name.trim() && createData.desc && createData.desc.trim()
    }
    async function createBlock() {
        let textFieldName: string
        switch (createData?.mode) {
            case 'html':
                textFieldName = 'content_html'
                break
            case 'markdown':
                textFieldName = 'md_content_mdown'
                break
            default:
                textFieldName = 'content_text'
                break
        }
        const data = {
            block_name: createData?.name,
            description: createData?.desc,
            edit_fields: [textFieldName],
            required_fields: [textFieldName],
        }
        try {
            const r = await simpleFetch('POST', jsonBaseUrl, data)
            sgoto(`/${singular.replace('_', '-')}/${r.data.doc_id}/${$currentLg}`)
        } catch (e) {
            console.error('Failed to create document:', e)
            notify({
                type: 'danger',
                title: $_('New document'),
                text: `Un nouveau document de type ${singular} n’a pas pu être créé.`,
            })
            creating = false
        }
    }
</script>

<Breadcrumb {crumbs} />

{#if loading}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else}
    <div class="card">
        <div class="card-header text-bg-primary">
            <div class="d-flex align-items-center">
                <div class="flex-grow-1">{$resolve(pageTitle)}</div>
                <div>
                    {#if $ugroups.has('dev')}
                        <button
                            class="btn btn-danger btn-sm"
                            type="button"
                            onclick={() => {
                                openCreateModal()
                            }}
                            disabled={updating}
                        >
                            <Icon inline icon="mdi:file-document-plus-outline" />
                            {$_('Ajouter un bloc')}
                        </button>
                    {/if}
                </div>
            </div>
        </div>

        <div class="card-body p-0">
            <table class="table table-hover mb-0" class:disabled-look={updating}>
                <thead class="table-secondary">
                    <tr>
                        <th>{$_('Description')}</th>
                        <th>{$_('Langues')}</th>
                    </tr>
                </thead>
                {#if !$results.length}
                    <tbody>
                        <tr><td colspan="3" class="text-center">{$_('No results')}</td></tr>
                    </tbody>
                {:else}
                    <tbody>
                        {#each $results as item (item.id)}
                            <tr>
                                <td>{item.description}</td>
                                <td>
                                    {#each $translatedSupportedLanguages as { lg, text }}
                                        <span class="enum">
                                            <Icon inline icon={item.lgs[lg].mainTag == 'online' ? 'mdi:checkbox-marked-outline' : 'mdi:checkbox-blank-outline'} />
                                            <a use:sanchor href={spath(`/${singular}/${item.id}/${lg}`)}>{text}</a>
                                        </span>
                                    {/each}
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                {/if}
            </table>
        </div>
    </div>

    <GenericListLegend />
{/if}

{#if showCreate && createData}
    <Modal
        close={() => {
            showCreate = false
        }}
        keep={() => {}}
    >
        <div class="modal-header text-bg-primary">{$_('Ajouter un bloc')}</div>

        <form
            onsubmit={(e) => {
                e.preventDefault()
                createBlock()
            }}
            novalidate
        >
            <div class="modal-body">
                <div class="mb-3">
                    <label for="block-name" class="form-label">{$_('ID interne du block')}</label>
                    <input type="text" class="form-control" id="block-name" aria-describedby="block-name-h" bind:value={createData.name} />
                    <div class="form-text" id="block-name-h">{$_('Cet identifiant est utilisé par le code du frontend pour retrouver le block.')}</div>
                </div>
                <div class="mb-3">
                    <label for="block-desc" class="form-label">{$_('Description')}</label>
                    <input type="text" class="form-control" id="block-desc" aria-describedby="block-desc-h" bind:value={createData.desc} />
                    <div class="form-text" id="block-desc-h">{$_('Affiché comme description dans l’admin pour aider à comprendre l’utilisation de ce block.')}</div>
                </div>
                <div>
                    <div class="form-check">
                        <input type="radio" class="form-check-input" id="block-mode-html" value="html" bind:group={createData.mode} />
                        <label for="block-mode-html" class="form-check-label">{$_('HTML')}</label>
                    </div>
                    <div class="form-check">
                        <input type="radio" class="form-check-input" id="block-mode-markdown" value="markdown" bind:group={createData.mode} />
                        <label for="block-mode-markdown" class="form-check-label">{$_('Markdown')}</label>
                    </div>
                    <div class="form-check">
                        <input type="radio" class="form-check-input" id="block-mode-text" value="text" bind:group={createData.mode} />
                        <label for="block-mode-text" class="form-check-label">{$_('Text')}</label>
                    </div>
                </div>
            </div>
            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary"
                    onclick={() => {
                        showCreate = false
                    }}>{$_('Fermer')}</button
                >
                <button type="submit" class="btn btn-sm btn-warning" disabled={!createIsValid(createData) || creating}>
                    {#if creating}
                        <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {$_('Un moment…')}
                    {:else}
                        {$_('Créer')}
                    {/if}
                </button>
            </div>
        </form>
    </Modal>
{/if}
